// frontend/src/components/assessment/artifacts/NarrationScript.js
// Phase 3 artifact: screenplay-style script editor.
// me:/them: turn pairs, inline editing, pair-level controls, ghost pair.
//
// Spec: Product/Hawthorne/MiscFeatures/ai-form-ui-spec.md, Section 5
//
// Drag-and-drop reorder is rendered (drag handles visible) but not wired.
// TODO: integrate @dnd-kit/core when approved — see spec Section 8.

import { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';
import useSpeechDictation from '../../../hooks/useSpeechDictation';

const EMPTY_PROMPT =
  "OK, let's write out how this conversation went — or how you'd like it to go. " +
  'You can type directly into the turns or dictate using the microphone (if your browser supports it). Let me know when you\'re done.';

let _pairSeq = 0;
const newClientPairId = () => `ghost_${Date.now()}_${_pairSeq++}`;

// ---------------------------------------------------------------------------
// ScriptTurnRow — one me: or them: line
// ---------------------------------------------------------------------------

const ScriptTurnRow = ({ speaker, content, source, placeholder, onSave, isDraggable, focusOnMount, pairId, isListening, interimTranscript, isMicSupported, onMicToggle }) => {
  const textareaRef = useRef(null);
  const [localValue, setLocalValue] = useState(content || '');
  const [isFocused, setIsFocused] = useState(false);
  const [highlighted, setHighlighted] = useState(false);
  const prevContentRef = useRef(content);

  // Auto-focus when a new pair is programmatically inserted
  useEffect(() => {
    if (focusOnMount && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Sync from parent when not actively editing (AI updates)
  useEffect(() => {
    if (!isFocused) setLocalValue(content || '');
  }, [content, isFocused]);

  // Brief highlight pulse when AI updates existing content
  useEffect(() => {
    if (
      source === 'ai' &&
      content && content.length > 0 &&
      prevContentRef.current && prevContentRef.current.length > 0 &&
      content !== prevContentRef.current
    ) {
      setHighlighted(true);
      const t = setTimeout(() => setHighlighted(false), 700);
      prevContentRef.current = content;
      return () => clearTimeout(t);
    }
    prevContentRef.current = content;
  }, [content, source]); 

  // Auto-expand textarea height to fit content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        Math.max(textareaRef.current.scrollHeight, 44) + 'px';
    }
  }, [localValue]);

  const rowClass = [
    'script-turn-row',
    `script-turn-${speaker}`,
    source === 'ai'   ? 'ai-sourced'   : '',
    source === 'user' ? 'user-sourced' : '',
    highlighted       ? 'highlighted'  : '',
    isListening       ? 'im-listening' : '',
  ].filter(Boolean).join(' ');

  // While dictating, show live preview without mutating localValue
  const displayValue = isListening && interimTranscript
    ? (localValue ? localValue + ' ' + interimTranscript : interimTranscript)
    : localValue;

  return (
    <div className={rowClass}>
      {isDraggable
        ? <span className="script-drag-handle" aria-hidden="true">⠿</span>
        : <span className="script-drag-spacer" />
      }
      <div className="im-label-col">
        <label
          htmlFor={`script-turn-${pairId}-${speaker}`}
          className={`script-speaker-label script-speaker-${speaker}`}
        >
          {speaker}:
        </label>
        {isMicSupported && (
          <button
            className={`im-mic-btn${isListening ? ' listening' : ''}`}
            title={isListening ? 'Stop dictating' : 'Dictate'}
            tabIndex={-1}
            onClick={() => onMicToggle(pairId, speaker)}
          >
            {isListening ? '■' : '🎤'}
          </button>
        )}
      </div>
      <textarea
        ref={textareaRef}
        id={`script-turn-${pairId}-${speaker}`}
        className={`script-turn-textarea${isFocused ? ' focused' : ''}`}
        rows={1}
        placeholder={isListening ? 'Listening…' : placeholder}
        value={displayValue}
        disabled={isListening}
        onChange={(e) => {
          setLocalValue(e.target.value);
          onSave(e.target.value);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          onSave(localValue);
        }}
      />
    </div>
  );
};

ScriptTurnRow.propTypes = {
  speaker:          PropTypes.oneOf(['me', 'them']).isRequired,
  content:          PropTypes.string,
  source:           PropTypes.oneOf(['none', 'ai', 'user']).isRequired,
  placeholder:      PropTypes.string.isRequired,
  onSave:           PropTypes.func.isRequired,
  isDraggable:      PropTypes.bool.isRequired,
  focusOnMount:     PropTypes.bool,
  pairId:           PropTypes.string.isRequired,
  isListening:      PropTypes.bool,
  interimTranscript: PropTypes.string,
  isMicSupported:   PropTypes.bool,
  onMicToggle:      PropTypes.func.isRequired,
};


// ---------------------------------------------------------------------------
// ScriptPair — one me/them pair with hover controls
// ---------------------------------------------------------------------------

const ScriptPair = ({ pair, pairIndex, isFirst, isLast, isNew, focusMeOnMount, onTurnSave, onDelete, onInsertAfter, onMoveUp, onMoveDown, activeMic, onMicToggle, interimTranscript, isMicSupported }) => {
  const pairClass = [
    'script-pair',
    isNew ? 'ai-appended' : '',
  ].filter(Boolean).join(' ');

  const isMeListening   = activeMic?.pairId === pair.pairId && activeMic?.side === 'me';
  const isThemListening = activeMic?.pairId === pair.pairId && activeMic?.side === 'them';

  return (
    <div className={pairClass}>
      <div className="script-pair-main">
        <ScriptTurnRow
          speaker="me"
          content={pair.me.content}
          source={pair.me.source}
          placeholder="What did you say?"
          isDraggable
          focusOnMount={focusMeOnMount}
          pairId={pair.pairId}
          isListening={isMeListening}
          interimTranscript={isMeListening ? interimTranscript : ''}
          isMicSupported={isMicSupported}
          onMicToggle={onMicToggle}
          onSave={(value) => onTurnSave(pair.pairId, 'me', value)}
        />
        <ScriptTurnRow
          speaker="them"
          content={pair.them.content}
          source={pair.them.source}
          placeholder="How did they respond?"
          isDraggable={false}
          pairId={pair.pairId}
          isListening={isThemListening}
          interimTranscript={isThemListening ? interimTranscript : ''}
          isMicSupported={isMicSupported}
          onMicToggle={onMicToggle}
          onSave={(value) => onTurnSave(pair.pairId, 'them', value)}
        />
      </div>

      <div className="script-pair-controls">
        <button
          className="script-pair-btn script-move-btn"
          title="Move up"
          disabled={isFirst}
          tabIndex={-1}
          onClick={() => onMoveUp(pairIndex)}
        >↑</button>
        <button
          className="script-pair-btn script-move-btn"
          title="Move down"
          disabled={isLast}
          tabIndex={-1}
          onClick={() => onMoveDown(pairIndex)}
        >↓</button>
        <button
          className="script-pair-btn script-delete-btn"
          title="Remove this exchange"
          tabIndex={-1}
          onClick={() => onDelete(pair.pairId, pairIndex)}
        >✕</button>
        <button
          className="script-pair-btn script-insert-btn"
          title="Insert exchange after this one"
          tabIndex={-1}
          onClick={() => onInsertAfter(pairIndex)}
        >+</button>
      </div>
    </div>
  );
};

ScriptPair.propTypes = {
  pair:          PropTypes.object.isRequired,
  pairIndex:     PropTypes.number.isRequired,
  isFirst:       PropTypes.bool,
  isLast:        PropTypes.bool,
  isNew:          PropTypes.bool,
  focusMeOnMount: PropTypes.bool,
  onTurnSave:     PropTypes.func.isRequired,
  onDelete:      PropTypes.func.isRequired,
  onInsertAfter: PropTypes.func.isRequired,
  onMoveUp:      PropTypes.func.isRequired,
  onMoveDown:    PropTypes.func.isRequired,
  activeMic:        PropTypes.shape({ pairId: PropTypes.string, side: PropTypes.string }),
  onMicToggle:      PropTypes.func.isRequired,
  interimTranscript: PropTypes.string,
  isMicSupported:   PropTypes.bool,
};


// ---------------------------------------------------------------------------
// GhostPair — trailing empty pair; commits on focus-leaves-container
// ---------------------------------------------------------------------------

const GhostPair = ({ onCommit }) => {
  const [meValue,   setMeValue]   = useState('');
  const [themValue, setThemValue] = useState('');
  const meRef         = useRef(null);
  const themRef       = useRef(null);
  const focusAfterRef = useRef(false);
  const [activeSide,  setActiveSide] = useState(null); // 'me' | 'them' | null

  const { isSupported: isMicSupported, isListening, interimTranscript, start: startDictation, stop: stopDictation } = useSpeechDictation();

  const handleMicToggle = useCallback((side) => {
    if (activeSide === side || isListening) {
      stopDictation();
      setActiveSide(null);
      return;
    }
    const existingContent = side === 'me' ? meValue : themValue;
    startDictation((finalText) => {
      const appended = existingContent
        ? existingContent.trimEnd() + ' ' + finalText
        : finalText;
      if (side === 'me') setMeValue(appended);
      else               setThemValue(appended);
      setActiveSide(null);
    });
    setActiveSide(side);
  }, [activeSide, isListening, meValue, themValue, startDictation, stopDictation]);

  const autoExpand = (ref) => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = Math.max(ref.current.scrollHeight, 44) + 'px';
    }
  };

  useEffect(() => autoExpand(meRef),   [meValue]);
  useEffect(() => autoExpand(themRef), [themValue]);

  // After commit clears meValue, focus the now-empty me: textarea
  useEffect(() => {
    if (focusAfterRef.current && meValue === '') {
      focusAfterRef.current = false;
      meRef.current?.focus();
    }
  }, [meValue]);

  // Commit the pair when focus leaves the ghost container entirely
  const handleContainerBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (meValue.trim()) {
        focusAfterRef.current = true;
        onCommit(meValue.trim(), themValue.trim() || null);
        setMeValue('');
        setThemValue('');
      }
    }
  };

  // them: is the last tabbable field on the page — Tabbing forward from here has
  // nowhere native to land, so the browser sends focus outside the document before
  // handleContainerBlur's refocus can reliably steal it back. Intercept it here so
  // focus never leaves the page at all.
  const handleThemKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey && meValue.trim()) {
      e.preventDefault();
      focusAfterRef.current = true;
      onCommit(meValue.trim(), themValue.trim() || null);
      setMeValue('');
      setThemValue('');
    }
  };

  const isMeListening   = activeSide === 'me'   && isListening;
  const isThemListening = activeSide === 'them'  && isListening;

  const meDisplay   = isMeListening   && interimTranscript
    ? (meValue   ? meValue.trimEnd()   + ' ' + interimTranscript : interimTranscript)
    : meValue;
  const themDisplay = isThemListening && interimTranscript
    ? (themValue ? themValue.trimEnd() + ' ' + interimTranscript : interimTranscript)
    : themValue;

  return (
    <div className="script-pair script-ghost-pair" onBlur={handleContainerBlur}>
      <div className="script-pair-main">
        <div className={`script-turn-row script-turn-me${isMeListening ? ' im-listening' : ''}`}>
          <span className="script-drag-spacer" />
          <div className="im-label-col">
            <label htmlFor="script-ghost-me" className="script-speaker-label script-speaker-me">me:</label>
            {isMicSupported && (
              <button
                className={`im-mic-btn${isMeListening ? ' listening' : ''}`}
                title={isMeListening ? 'Stop dictating' : 'Dictate'}
                tabIndex={-1}
                onClick={() => handleMicToggle('me')}
              >
                {isMeListening ? '■' : '🎤'}
              </button>
            )}
          </div>
          <textarea
            ref={meRef}
            id="script-ghost-me"
            className="script-turn-textarea"
            rows={1}
            placeholder={isMeListening ? 'Listening…' : 'What will you say?'}
            value={meDisplay}
            disabled={isMeListening}
            onChange={(e) => setMeValue(e.target.value)}
          />
        </div>
        <div className={`script-turn-row script-turn-them${isThemListening ? ' im-listening' : ''}`}>
          <span className="script-drag-spacer" />
          <div className="im-label-col">
            <label htmlFor="script-ghost-them" className="script-speaker-label script-speaker-them">them:</label>
            {isMicSupported && (
              <button
                className={`im-mic-btn${isThemListening ? ' listening' : ''}`}
                title={isThemListening ? 'Stop dictating' : 'Dictate'}
                tabIndex={-1}
                onClick={() => handleMicToggle('them')}
              >
                {isThemListening ? '■' : '🎤'}
              </button>
            )}
          </div>
          <textarea
            ref={themRef}
            id="script-ghost-them"
            className="script-turn-textarea"
            rows={1}
            placeholder={isThemListening ? 'Listening…' : 'How might they respond?'}
            value={themDisplay}
            disabled={isThemListening}
            onChange={(e) => setThemValue(e.target.value)}
            onKeyDown={handleThemKeyDown}
          />
        </div>
      </div>
    </div>
  );
};

GhostPair.propTypes = {
  onCommit: PropTypes.func.isRequired,
};


// ---------------------------------------------------------------------------
// UndoToast
// ---------------------------------------------------------------------------

const UndoToast = ({ onUndo }) => (
  <div className="script-undo-toast">
    <span>Exchange removed.</span>
    <button className="script-undo-btn" onClick={onUndo}>Undo</button>
  </div>
);

UndoToast.propTypes = {
  onUndo: PropTypes.func.isRequired,
};


// ---------------------------------------------------------------------------
// NarrationScript — exported main component
// ---------------------------------------------------------------------------

const NarrationScript = forwardRef(({
  script,
  conversationId,
  onTurnChange,
  onScriptReplace,
  onSaveStatusChange,
}, ref) => {
  const { user } = useAuth();
  const bottomRef    = useRef(null);
  const saveTimerRef = useRef(null);
  const prevScriptRef = useRef(script);
  const scriptRef     = useRef(script); // always holds current script for timer callbacks

  const [undoPending,       setUndoPending]       = useState(null); // { pair, index, timerId }
  const [newPairIds,        setNewPairIds]         = useState(new Set());
  const [pendingFocusPairId, setPendingFocusPairId] = useState(null);
  const [activeMic,         setActiveMic]          = useState(null); // { pairId, side }

  const { isSupported: isMicSupported, isListening, interimTranscript, start: startDictation, stop: stopDictation } = useSpeechDictation();

  // Keep scriptRef current so timer callbacks always see the latest state
  useEffect(() => { scriptRef.current = script; }, [script]);

  // Detect AI-appended pairs: animate and auto-scroll
  useEffect(() => {
    const prev = prevScriptRef.current;
    if (script.length > prev.length) {
      const added = script.slice(prev.length);
      setNewPairIds(new Set(added.map(p => p.pairId)));
      const clearTimer = setTimeout(() => setNewPairIds(new Set()), 400);
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      prevScriptRef.current = script;
      return () => clearTimeout(clearTimer);
    }
    prevScriptRef.current = script;
  }, [script]);

  // Debounced autosave — always reads scriptRef.current so rapid onChange calls
  // always capture the latest state, matching the CaseStudyForm save pattern.
  //
  // doSave performs the PATCH now; scheduleSave debounces it; flush() (exposed to the
  // parent via ref) forces a pending save to complete synchronously before an AI turn,
  // so the server never processes a message against stale script state and can't have
  // its write clobbered by a late autosave. See AssessmentView.handleSendMessage.
  const dirtyRef    = useRef(false);   // unsaved edits pending?
  const inflightRef = useRef(null);    // promise of the in-flight PATCH, if any

  const doSave = useCallback(() => {
    dirtyRef.current = false;
    onSaveStatusChange('saving');
    const pairs = scriptRef.current;
    const req = apiService.patch(
      `/api/v1/conversations/${conversationId}/script`,
      {
        pairs: pairs.map(p => ({
          pairId: p.pairId,
          me:     p.me,
          them:   p.them,
          ...(p.me_thought   ? { me_thought:   p.me_thought   } : {}),
          ...(p.them_thought ? { them_thought: p.them_thought } : {}),
        })),
      },
      user
    )
      .then(() => {
        onSaveStatusChange('saved');
        setTimeout(() => onSaveStatusChange(null), 2000);
      })
      .catch((err) => {
        console.error('[NarrationScript] Autosave failed:', err);
        onSaveStatusChange(null);
      });
    inflightRef.current = req;
    return req;
  }, [conversationId, user, onSaveStatusChange]);

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(doSave, 1000);
  }, [doSave]);

  // Force any pending/in-flight save to settle. Resolves once the DB reflects the
  // editor's current state (or immediately if there is nothing outstanding).
  const flush = useCallback(async () => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (dirtyRef.current) return doSave();
    if (inflightRef.current) return inflightRef.current;
    return undefined;
  }, [doSave]);

  useImperativeHandle(ref, () => ({ flush }), [flush]);

  const handleTurnSave = useCallback((pairId, side, value) => {
    onTurnChange(pairId, side, value);
    scheduleSave();
  }, [onTurnChange, scheduleSave]);

  const handleDelete = useCallback((pairId, pairIndex) => {
    const pair = script[pairIndex];
    // Optimistic remove
    onScriptReplace(script.filter(p => p.pairId !== pairId));

    const timerId = setTimeout(() => {
      // Undo window expired — scriptRef has the latest state including this removal
      scheduleSave();
      setUndoPending(null);
    }, 5000);

    setUndoPending({ pair, index: pairIndex, timerId });
  }, [script, onScriptReplace, scheduleSave]);

  const handleUndo = useCallback(() => {
    if (!undoPending) return;
    clearTimeout(undoPending.timerId);
    const restored = [...script];
    restored.splice(undoPending.index, 0, undoPending.pair);
    onScriptReplace(restored);
    setUndoPending(null);
  }, [undoPending, script, onScriptReplace]);

  const handleMoveUp = useCallback((index) => {
    if (index === 0) return;
    const next = [...script];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onScriptReplace(next);
    scheduleSave();
  }, [script, onScriptReplace, scheduleSave]);

  const handleMoveDown = useCallback((index) => {
    if (index >= script.length - 1) return;
    const next = [...script];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onScriptReplace(next);
    scheduleSave();
  }, [script, onScriptReplace, scheduleSave]);

  const handleInsertAfter = useCallback((afterIndex) => {
    const inserted = {
      pairId: newClientPairId(),
      me:   { content: null, source: 'none' },
      them: { content: null, source: 'none' },
    };
    const next = [...script];
    next.splice(afterIndex + 1, 0, inserted);
    onScriptReplace(next);
    scheduleSave();
    setPendingFocusPairId(inserted.pairId);
  }, [script, onScriptReplace, scheduleSave]);

  const handleMicToggle = useCallback((pairId, side) => {
    const isThisActive = activeMic?.pairId === pairId && activeMic?.side === side;
    if (isThisActive || isListening) {
      stopDictation();
      setActiveMic(null);
      return;
    }
    const pair = scriptRef.current.find(p => p.pairId === pairId);
    const existingContent = pair?.[side]?.content || '';

    startDictation((finalText) => {
      const appended = existingContent
        ? existingContent.trimEnd() + ' ' + finalText
        : finalText;
      onTurnChange(pairId, side, appended);
      scheduleSave();
      setActiveMic(null);
    });
    setActiveMic({ pairId, side });
  }, [activeMic, isListening, startDictation, stopDictation, onTurnChange, scheduleSave]);

  const handleGhostCommit = useCallback((meContent, themContent) => {
    const newPair = {
      pairId: newClientPairId(),
      me:   { content: meContent,   source: 'user' },
      them: { content: themContent, source: themContent ? 'user' : 'none' },
    };
    const next = [...script, newPair];
    onScriptReplace(next);
    scheduleSave();
  }, [script, onScriptReplace, scheduleSave]);

  return (
    <div className="narration-script">
      {script.length === 0 && (
        <p className="script-empty-prompt">{EMPTY_PROMPT}</p>
      )}

      {script.map((pair, idx) => (
        <ScriptPair
          key={pair.pairId}
          pair={pair}
          pairIndex={idx}
          isFirst={idx === 0}
          isLast={idx === script.length - 1}
          isNew={newPairIds.has(pair.pairId)}
          focusMeOnMount={pair.pairId === pendingFocusPairId}
          onTurnSave={handleTurnSave}
          onDelete={handleDelete}
          onInsertAfter={handleInsertAfter}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          activeMic={activeMic}
          onMicToggle={handleMicToggle}
          interimTranscript={interimTranscript}
          isMicSupported={isMicSupported}
        />
      ))}

      <GhostPair onCommit={handleGhostCommit} />
      <div ref={bottomRef} />

      {undoPending && <UndoToast onUndo={handleUndo} />}
    </div>
  );
});

NarrationScript.displayName = 'NarrationScript';

NarrationScript.propTypes = {
  script: PropTypes.arrayOf(PropTypes.shape({
    pairId: PropTypes.string.isRequired,
    me:     PropTypes.shape({ content: PropTypes.string, source: PropTypes.oneOf(['none', 'ai', 'user']) }).isRequired,
    them:   PropTypes.shape({ content: PropTypes.string, source: PropTypes.oneOf(['none', 'ai', 'user']) }).isRequired,
  })).isRequired,
  conversationId:     PropTypes.string.isRequired,
  onTurnChange:       PropTypes.func.isRequired,
  onScriptReplace:    PropTypes.func.isRequired,
  onSaveStatusChange: PropTypes.func.isRequired,
};

export default NarrationScript;